import { useCallback, useMemo, useState } from 'react';

import type { FormStepId } from 'shared/config/formStepId';
import { useAppSelector } from 'shared/lib';
import { logFormData } from 'shared/lib/logFormData';

import type { TCreditFormCommonFields } from 'entities/creditForm';
import { isLoggedSelector } from 'entities/user';

import { useSendLoansContactData } from 'features/SendLoansContactData';
import { useSendPreApprovalsSearchApplication } from 'features/SendPreApprovalsSearchApplication';

import { useStepper } from '../stepper';

import { getValidationSteps } from './getValidationSteps';
import { useValidateData } from './useValidateData';

type TAutoValidationOptions = {
  onFormValidationSuccess?: (fields: TCreditFormCommonFields) => void;
  initialOpenStepId?: ValuesOf<typeof FormStepId>; // stepId, который нужно сразу открыть, если шаги до этого валидны
  isSendContact?: boolean;
  isSendPreApprovalSearch?: boolean;
} & Pick<Parameters<typeof useValidateData>[0], 'onStepSuccess' | 'onComplete' | 'onError' | 'analyticsOverrides'>;

/**
 * Хук для автоматической валидации анкеты.
 * Отправляет Контакт и pre-approvals search после успешной валидации паспортного блока.
 * Использовать на уровне pages
 *
 * @param isEnabledValidationOnMount - запустить валидацию на mount
 * @param options
 * @param options.onFormValidationSuccess - колбэк при успешной валидации всей формы
 * @param options.initialOpenStepId - шаг, который нужно сразу раскрыть, если предыдущие валидны
 * @param options.onStepSuccess - колбэк при успешной валидации шага
 * @param options.onComplete - колбэк по окончании всей валидации (успех или ошибка)
 * @param options.onError - колбэк при ошибке валидации
 * @param options.analyticsOverrides - переопределение параметров GA-событий
 * @param options.isSendContact - отправлять Контакт
 * @param options.isSendPreApprovalSearch - отправлять PreApprovalSearch
 */
export const useAutoValidation = (isEnabledValidationOnMount = true, options?: TAutoValidationOptions) => {
  const {
    analyticsOverrides,
    onFormValidationSuccess,
    initialOpenStepId,
    onStepSuccess,
    onComplete,
    onError,
    isSendContact = true,
    isSendPreApprovalSearch = true,
  } = options || {};

  const isAuth = useAppSelector(isLoggedSelector);
  const sendContact = useSendLoansContactData();
  const sendPreApprovalsSearchApplication = useSendPreApprovalsSearchApplication();
  const { lastStep, stepsWithMultiStepsOrder } = useStepper();

  const [isFirstValidation, setIsFirstValidation] = useState(true);

  const stepsToValidate = getValidationSteps(stepsWithMultiStepsOrder, {
    isAuth,
    initialOpenStepId,
    isFirstValidation,
  });

  const onStepValidationSuccess = useCallback(
    async (stepId: ValuesOf<typeof FormStepId>) => {
      if (stepId === 'passportInfo' && isAuth) {
        if (isSendContact) {
          await sendContact();
        }
        if (isSendPreApprovalSearch) {
          await sendPreApprovalsSearchApplication();
        }
      }
      onStepSuccess?.(stepId);
    },
    [isAuth, onStepSuccess, isSendContact, isSendPreApprovalSearch, sendContact, sendPreApprovalsSearchApplication],
  );

  const onFullFormValidationSuccess = useCallback(
    async (data: TCreditFormCommonFields, lastValidatedStepId: ValuesOf<typeof FormStepId>) => {
      const isLastStepValidated = lastValidatedStepId === lastStep.formId;
      if (!isLastStepValidated) {
        return;
      }

      logFormData('Result Form SubmitData', data);

      onFormValidationSuccess?.(data);
    },
    [onFormValidationSuccess, lastStep.formId],
  );

  const onValidationComplete = useCallback(() => {
    setIsFirstValidation(false);
    onComplete?.();
  }, [onComplete]);

  const { validate } = useValidateData(
    {
      analyticsOverrides,
      onComplete: onValidationComplete,
      onSuccess: onFullFormValidationSuccess,
      onStepSuccess: onStepValidationSuccess,
      onError,
      stepsToValidate,
    },
    isEnabledValidationOnMount && !!stepsToValidate.length,
  );

  return useMemo(() => ({ validate }), [validate]);
};
